import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogTitle,
  TextField,
} from '@mui/material';
import DivForm from 'src/components/ModalDialogMUI6/styled/DivForm';
import Spinner from 'src/components/Spinner/Spinner';
import {
  getFollowUpMessages,
  clearFollowUpMessages,
} from 'src/actions/chatActions';
import helpers from 'src/helpers';
import { STYLE_OPTIONS } from 'src/constants';

const filterOptions = createFilterOptions({
  stringify: ({
    label, description
  }) => `${label} ${description}`
});

const isOptionEqualToValue = (option, value) => value?.value && value?.value === option?.value;

const renderOptionAutocomplete = (props, option) => (
  <div key={option} {...props} style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
    <b>{option.label}</b>
    <Box
      sx={{
        color: STYLE_OPTIONS.COLOR_BLUE,
        fontFamily: STYLE_OPTIONS.FONT_FAMILY,
        fontSize: '12px',
      }}
    >
      {option.description && (
        <div>
          {option.description.replace(/(\r\n|\n|\r)/gm, ', ')}
        </div>
      )}
    </Box>
  </div>
);

function ModalInsertDynamicFields({
  isModalVisible,
  followUpMessages,
  onApply,
  onCloseModal,
}) {
  const [selectedField, setSelectedField] = useState(null);
  const dispatch = useDispatch();

  const {
    followUpMessageList,
    loading: isFollowUpMessagesLoading,
  } = followUpMessages;

  const optionList = useMemo(() => {
    const followUpMessageListOptions = followUpMessageList?.map((followUpMessage) => ({
      value: followUpMessage.id,
      label: followUpMessage.code,
      description: followUpMessage.description,
    })) || [];

    return helpers.transform.arrayStringToObjectLabelValue(followUpMessageListOptions);
  }, [followUpMessageList]);

  const handleClose = () => {
    onCloseModal();
    setSelectedField(null);
  };

  const handleApply = () => {
    const selectedTemplate = selectedField?.value || null;
    if (selectedTemplate) {
      onApply(selectedTemplate);
    }
    handleClose();
  };

  useEffect(() => {
    if (isModalVisible) {
      dispatch(getFollowUpMessages());
    }

    return () => {
      dispatch(clearFollowUpMessages());
    };
  }, [dispatch, isModalVisible]);

  return (
    <Dialog
      open={isModalVisible}
      maxWidth="sm"
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          padding: '24px',
        },
      }}
      fullWidth
    >
      <Spinner
        loading={isFollowUpMessagesLoading}
        positionType="absolute"
      />
      <Box
        sx={{
          padding: '0 0 0 16px',
          textAlign: 'center',
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '18px',
            padding: 0,
          }}
        >
          Enter & Select Template
        </DialogTitle>
      </Box>
      <DivForm>
        <Autocomplete
          options={optionList || []}
          value={selectedField}
          filterOptions={filterOptions}
          renderOption={renderOptionAutocomplete}
          isOptionEqualToValue={isOptionEqualToValue}
          onChange={(e, value) => setSelectedField(value)}
          renderInput={(params) => (
            <TextField
              label="Message Template & Scenario"
              placeholder="Enter & Select Template"
              {...params}
            />
          )}
          handleHomeEndKeys
          selectOnFocus
          clearOnBlur
        />
      </DivForm>
      <DialogActions
        sx={{
          padding: 0,
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            ...STYLE_OPTIONS.BUTTON_PRIMARY_REVERSED,
            minWidth: '105px',
            marginRight: '14px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          disabled={!selectedField?.value}
          sx={{
            ...STYLE_OPTIONS.BUTTON_PRIMARY,
            minWidth: '105px',
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ModalInsertDynamicFields.propTypes = {
  isModalVisible: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  followUpMessages: PropTypes.object,
};
ModalInsertDynamicFields.defaultProps = {
  followUpMessages: {
    followUpMessageList: [],
    loading: false,
  }
};

export default ModalInsertDynamicFields;
